import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { EmergingMarket } from '@/creditflowresearch/domain/Market';
import { CorpSegment, FigSegment, SsaSegment } from '@/creditflowresearch/domain/Segment';
import Preferences from '@/creditflowresearch/domain/Preferences/Preferences';
import { PreferenceFamily } from '@/creditflowresearch/domain/Preferences/PreferenceSource';
import { hasRegionAccess, type IRegionAccessControl } from '@/creditflowresearch/domain/Region/selectors/hasRegionAccess';
import { selectPreferenceQueryParts } from '@/creditflowresearch/application/Preferences/selectPreferenceQueryParts';
import { toRegionAbbreviations } from '@/creditflowresearch/application/Preferences/selectAccessibleMappedIds';
import { EM_FILTER_REGION_IDS } from '@/creditflowresearch/application/PageFilters/constants';
import {
  getCorporatePageDefaultParams,
  getCorporatePageProductId,
  getDefaultCorporateSegments,
} from '@/creditflowresearch/application/helpers/getCorporatePageDefaultParams';
import { getProductsRegionsDefaults } from '@/creditflowresearch/application/helpers/validators/getProductsRegionsDefaults';
import type { ApplicationContext } from '@/creditflowresearch/application/withApplicationPropsV2';
import EmeaRegion from '@/creditflowresearch/domain/Region/entities/EmeaRegion';

const toAccessControl = (context: ApplicationContext): IRegionAccessControl => ({
  plan: context.userPlanState.plan,
  accessibleRegionsIds: context.userPlanState.regions || [],
});

const toPreferencesWhenMappingApplies = (context: ApplicationContext): Preferences | null => {
  const { plan, preferences } = context.userPlanState;

  if (!UserPlan.isSatisfiedPremiumPlan(plan) || !preferences) {
    return null;
  }

  return new Preferences(preferences);
};

// TODO: remove when the European market opening period ends; CEEMEA then locks with the rest
const selectOpenEmRegionIds = (context: ApplicationContext): string[] => {
  const accessControl = toAccessControl(context);

  return EM_FILTER_REGION_IDS.filter((regionId) =>
    hasRegionAccess(regionId, accessControl) || regionId === EmeaRegion.REGION_ID);
};

// TODO: remove when the European market opening period ends; without it non-premium
const hasGranted = (context: ApplicationContext): boolean => {
  const { plan } = context.userPlanState;

  return UserPlan.isStarter(plan) || UserPlan.isUnauthorized(plan);
};

export const getAbsPreferenceDefaults = (context: ApplicationContext): Record<string, string> => ({
  ...getProductsRegionsDefaults(context),
  ...selectPreferenceQueryParts(
    toPreferencesWhenMappingApplies(context),
    toAccessControl(context),
    { family: PreferenceFamily.ABS },
  ),
});

export const getCorporatePreferenceDefaults = (
  context: ApplicationContext,
  url: string,
): Record<string, string> => {
  const defaults = getCorporatePageDefaultParams(url, hasGranted(context));

  const parts = selectPreferenceQueryParts(
    toPreferencesWhenMappingApplies(context),
    toAccessControl(context),
    {
      family: PreferenceFamily.CORPORATE,
      productId: getCorporatePageProductId(url),
      fallbackMarkets: defaults.markets,
    },
  );

  return {
    ...defaults,
    ...parts,
    // A preference can move the markets without saving a segment group for this product; the
    // default combination then has to follow the markets it landed on (US-5785).
    segments: parts.segments ?? getDefaultCorporateSegments(
      defaults.products,
      parts.markets ?? defaults.markets,
    ),
  };
};

export const getEmPreferenceDefaults = (context: ApplicationContext): Record<string, string> => {
  const defaults: Record<string, string> = {
    markets: EmergingMarket.ABBREVIATION,
    // TODO: remove when the European market opening period ends; reverts to EM_FILTER_REGION_IDS
    regions: hasGranted(context)
      ? EmeaRegion.ABBREVIATION
      : toRegionAbbreviations(selectOpenEmRegionIds(context)),
    segments: [CorpSegment.TYPE, SsaSegment.TYPE, FigSegment.TYPE].toString(),
  };

  return {
    ...defaults,
    ...selectPreferenceQueryParts(
      toPreferencesWhenMappingApplies(context),
      toAccessControl(context),
      { family: PreferenceFamily.EM },
    ),
  };
};
